import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Building2, CreditCard, LayoutDashboard, LogOut, Receipt, ShieldCheck, Settings,
} from 'lucide-react';
import { saasApi } from '../../utils/saasApi';

export default function SuperAdminLayout() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await saasApi.me();
        if (!cancelled) setAdmin(me);
      } catch (_) {
        if (!cancelled) navigate('/superadmin/login', { replace: true });
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await saasApi.logout();
    navigate('/superadmin/login', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <p className="text-sm text-zinc-500">Loading platform console…</p>
      </div>
    );
  }

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
      isActive
        ? 'bg-amber-50 text-amber-900 border border-amber-200'
        : 'text-zinc-600 hover:bg-zinc-100'
    }`;

  return (
    <div className="min-h-screen flex bg-zinc-50" data-testid="super-admin-layout">
      <aside className="w-64 border-r bg-white flex flex-col">
        <div className="px-5 py-5 border-b">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            <span
              className="font-bold text-zinc-800"
              style={{ fontFamily: 'Manrope' }}
            >
              Platform Console
            </span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">SaaS owner ops</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          <NavLink to="/superadmin/overview" className={linkClass} data-testid="sa-nav-overview">
            <LayoutDashboard className="h-4 w-4" /> Overview
          </NavLink>
          <NavLink to="/superadmin/tenants" className={linkClass} data-testid="sa-nav-tenants">
            <Building2 className="h-4 w-4" /> Tenants
          </NavLink>
          <NavLink to="/superadmin/plans" className={linkClass} data-testid="sa-nav-plans">
            <CreditCard className="h-4 w-4" /> Plans
          </NavLink>
          <NavLink to="/superadmin/billing" className={linkClass} data-testid="sa-nav-billing">
            <Receipt className="h-4 w-4" /> Billing
          </NavLink>
          <NavLink to="/superadmin/settings" className={linkClass} data-testid="sa-nav-settings">
            <Settings className="h-4 w-4" /> Platform settings
          </NavLink>
        </nav>
        <div className="p-3 border-t">
          <div className="text-[11px] text-zinc-500 mb-1">Signed in as</div>
          <div className="text-sm font-medium text-zinc-800 truncate">
            {admin?.email}
          </div>
          <button
            onClick={logout}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
            data-testid="sa-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
